import React, { useState } from 'react';

interface AssetData {
  id: string;
  name: string;
  symbol: string;
  owner: string;
  image: string;
  animationUrl: string;
  metadata: any;
}

export default function DevChain() {
  const [mintAddress, setMintAddress] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [asset, setAsset] = useState<AssetData | null>(null);
  const [showModel, setShowModel] = useState(false);

  const fetchAsset = async () => {
    if (!mintAddress.trim()) {
      setError('Please enter a mint address');
      return;
    }

    setLoading(true);
    setError('');
    setAsset(null);

    try {
      // Fetch asset from Helius or your backend API
      const rpcUrl = import.meta.env.HELIUS_RPC_URL || 'https://api.devnet.solana.com';
      const apiKey = import.meta.env.HELIUS_API_KEY || '';
      
      let url = rpcUrl;
      if (apiKey && !rpcUrl.includes('api-key=')) {
        url = `https://devnet.helius-rpc.com/?api-key=${apiKey}`;
      }

      const response = await fetch(url, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({
          jsonrpc: '2.0',
          id: 'dev-chain',
          method: 'getAsset',
          params: { id: mintAddress.trim() }
        })
      });

      const data = await response.json();

      if (data.error) {
        throw new Error(data.error.message || 'Failed to fetch asset');
      }

      const result = data.result;
      if (!result) {
        throw new Error('No asset data returned');
      }

      setAsset({
        id: result.id,
        name: result.content?.metadata?.name || 'Unknown',
        symbol: result.content?.metadata?.symbol || 'N/A',
        owner: result.ownership?.owner || 'Unknown',
        image: result.content?.links?.image || result.content?.files?.[0]?.uri || '',
        animationUrl: result.content?.links?.animation_url || '',
        metadata: result
      });
    } catch (err: any) {
      setError(err.message || 'Failed to fetch asset');
      console.error('Fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      fetchAsset();
    }
  };

  return (
    <div className="min-h-screen bg-black text-white p-8">
      <div className="max-w-4xl mx-auto">
        <h1 className="text-4xl font-bold mb-2">🔗 Devnet Chain Tester</h1>
        <p className="text-gray-400 mb-8">
          Test NFT fetching and 3D rendering from Solana devnet
        </p>

        {/* Input Section */}
        <div className="bg-gray-900 rounded-lg p-6 mb-6">
          <label className="block text-sm font-medium mb-2">
            NFT Mint Address
          </label>
          <div className="flex gap-2">
            <input
              type="text"
              value={mintAddress}
              onChange={(e) => setMintAddress(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Enter mint address (e.g., 7xKXt...)"
              className="flex-1 bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 focus:outline-none focus:border-purple-500"
            />
            <button
              onClick={fetchAsset}
              disabled={loading}
              className="bg-purple-600 hover:bg-purple-700 disabled:bg-gray-700 px-6 py-2 rounded-lg font-medium transition"
            >
              {loading ? 'Loading...' : 'Fetch'}
            </button>
          </div>
          {error && (
            <p className="text-red-400 text-sm mt-2">❌ {error}</p>
          )}
        </div>

        {/* Results Section */}
        {asset && (
          <div className="space-y-6">
            {/* Asset Card */}
            <div className="bg-gray-900 rounded-lg overflow-hidden">
              <div className="grid md:grid-cols-2 gap-6 p-6">
                {/* Image */}
                <div>
                  {asset.image ? (
                    <img
                      src={asset.image}
                      alt={asset.name}
                      className="w-full h-64 object-cover rounded-lg"
                    />
                  ) : (
                    <div className="w-full h-64 bg-gray-800 rounded-lg flex items-center justify-center">
                      <span className="text-gray-500">No image</span>
                    </div>
                  )}
                </div>

                {/* Info */}
                <div className="space-y-4">
                  <div>
                    <h2 className="text-2xl font-bold">{asset.name}</h2>
                    <p className="text-gray-400">{asset.symbol}</p>
                  </div>

                  <div className="space-y-2">
                    <div>
                      <span className="text-gray-400 text-sm">Mint Address</span>
                      <p className="font-mono text-xs break-all">{asset.id}</p>
                    </div>
                    <div>
                      <span className="text-gray-400 text-sm">Owner</span>
                      <p className="font-mono text-xs break-all">{asset.owner}</p>
                    </div>
                  </div>

                  {asset.animationUrl && (
                    <button
                      onClick={() => setShowModel(true)}
                      className="w-full bg-blue-600 hover:bg-blue-700 px-4 py-2 rounded-lg font-medium transition"
                    >
                      View 3D Model
                    </button>
                  )}
                </div>
              </div>
            </div>

            {/* Metadata JSON */}
            <details className="bg-gray-900 rounded-lg p-6">
              <summary className="cursor-pointer font-medium">
                📄 Full Metadata (JSON)
              </summary>
              <pre className="mt-4 text-xs overflow-auto bg-gray-800 p-4 rounded">
                {JSON.stringify(asset.metadata, null, 2)}
              </pre>
            </details>
          </div>
        )}

        {/* 3D Model Popup */}
        {showModel && asset?.animationUrl && (
          <div
            className="fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center z-50"
            onClick={() => setShowModel(false)}
          >
            <div
              className="relative bg-gray-900 rounded-lg p-4 max-w-4xl w-full mx-4"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                onClick={() => setShowModel(false)}
                className="absolute top-4 right-4 text-white hover:text-gray-300 text-2xl"
              >
                ×
              </button>
              <h3 className="text-xl font-bold mb-4">{asset.name} - 3D Model</h3>
              <model-viewer
                src={asset.animationUrl}
                alt={asset.name}
                auto-rotate
                camera-controls
                style={{ width: '100%', height: '500px' }}
                className="bg-gray-800 rounded"
              />
            </div>
          </div>
        )}

        {/* Instructions */}
        <div className="mt-12 bg-gray-900 rounded-lg p-6">
          <h3 className="text-lg font-bold mb-4">📋 How to Use</h3>
          <ol className="list-decimal list-inside space-y-2 text-gray-300">
            <li>Run <code className="bg-gray-800 px-2 py-1 rounded">npm run mint</code> to mint a test NFT</li>
            <li>Copy the mint address from the console output</li>
            <li>Paste it above and click "Fetch"</li>
            <li>View the NFT card and click "View 3D Model" if available</li>
            <li>Test transfers with <code className="bg-gray-800 px-2 py-1 rounded">npm run transfer &lt;mint&gt; &lt;recipient&gt;</code></li>
          </ol>
        </div>
      </div>
    </div>
  );
}
